import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { AdCampaignTypeEnum } from '@entities/media';
import type { PromoUploadStatusType } from '@entities/adCampaign';
import { AdCampaignMediaTypeEnum } from '@shared/api/graphql/generated';

import { CREATE_AD_CAMPAIGN_SLICE_NAME, CREATE_AD_CAMPAIGN_STEPS, createAdCampaignDefaultState } from './consts';
import type { ICreateAdCampaignWeekdays, ICreateAdCampaignSelectedTag } from './types';

export const createAdCampaignSlice = createSlice({
  name: CREATE_AD_CAMPAIGN_SLICE_NAME,
  initialState: createAdCampaignDefaultState,
  reducers: {
    setStep: (state, action: PayloadAction<CREATE_AD_CAMPAIGN_STEPS>) => {
      state.step = action.payload;
    },

    setCampaignType: (state, action: PayloadAction<AdCampaignTypeEnum>) => {
      state.campaignType = action.payload;
    },

    setCampaignName: (state, action: PayloadAction<string>) => {
      state.campaignName = action.payload;
    },

    setValueByMode: (state, action: PayloadAction<string>) => {
      state.valueByMode = action.payload;
    },

    setStartDate: (state, action: PayloadAction<string | null>) => {
      state.startDate = action.payload;
    },

    setEndDate: (state, action: PayloadAction<string | null>) => {
      state.endDate = action.payload;
    },

    setIsInfinitely: (state, action: PayloadAction<boolean>) => {
      state.isInfinitely = action.payload;
    },

    setWeekday: (state, action: PayloadAction<{ key: keyof ICreateAdCampaignWeekdays; value: boolean }>) => {
      state.weekdays[action.payload.key] = action.payload.value;
    },

    setDayStartTime: (state, action: PayloadAction<string | undefined>) => {
      state.dayStartTime = action.payload ?? '';
    },

    setDayEndTime: (state, action: PayloadAction<string | undefined>) => {
      state.dayEndTime = action.payload ?? '';
    },

    setMediaType: (state, action: PayloadAction<AdCampaignMediaTypeEnum>) => {
      state.mediaType = action.payload;
    },

    setSelectedTags: (state, action: PayloadAction<ICreateAdCampaignSelectedTag[]>) => {
      state.selectedTags = action.payload;
    },

    setSelectedPointIds: (state, action: PayloadAction<string[]>) => {
      state.selectedPointIds = action.payload;
    },

    setPromoStatuses: (state, action: PayloadAction<PromoUploadStatusType[]>) => {
      state.promoStatuses = action.payload;
    },

    setPromoTimesById: (state, action: PayloadAction<Record<string, string>>) => {
      state.promoTimesById = action.payload;
    },

    resetCreateAdCampaign: () => ({ ...createAdCampaignDefaultState }),
  },
});

export const {
  resetCreateAdCampaign,
  setCampaignType,
  setStep,
  setCampaignName,
  setValueByMode,
  setStartDate,
  setEndDate,
  setIsInfinitely,
  setWeekday,
  setDayStartTime,
  setDayEndTime,
  setMediaType,
  setSelectedTags,
  setSelectedPointIds,
  setPromoStatuses,
  setPromoTimesById,
} = createAdCampaignSlice.actions;

export const createAdCampaignReducer = createAdCampaignSlice.reducer;
